import { Form, Button } from "react-bulma-components";
import { useRef, useEffect, useState, useCallback, useMemo } from "react";

import Autosuggest from "react-autosuggest";
import { defaultTheme } from "react-autosuggest/dist/theme";

import { toLowercaseAscii } from "util/trimAccents.js";

const { Input, Field, Control } = Form;

/* Input component that auto-corrects to remove initial/trailing spaces,
   collapse multiple spaces to one, and perform any other requested
   on-the-fly value corrections.

   In order to keep track of insertion point and selection, the input is
   split into pre-selection, selection, post-selection segments and
   corrections are applied separately, in order, to each segment. Each
   correction is specified as a function (segment, state) => correctedSegment
   that may use the passed "state" object (initially empty) to record
   any information it needs to remember about its action on the previous
   segment.

   If the elementProps include inputMode="numeric", a correction function to
   ensure numeric digits only is automatically added if otherCorrections
   is empty. (NOTE: there is no point doing this if type="number", for then
   the browser itself does the constraint validation and the "value" we
   see never has any non-numeric characters. However, we avoid using
   type="number" most of the time, because it can be confusing when the
   user can type non-numeric characters that get ignored by the browser
   and this code can't even see them to generate a warning).

*/

function TrimmedInput({
  value,
  setValue,
  otherCorrections = [],
  startFocussed,
  setFinished,
  setFinishedValue,
  autosuggest,
  isCurrency,
  ...elementProps
}) {
  const ref = useRef(null);

  // We may want to allow the displayed value to be different from what the
  // caller has passed as the input value.

  // If someone types a space at the end of the input, or a decimal point at
  // the end of a numeric input value, or a decimal point followed by a
  // sequence of zeros, we trim it off what we pass to the caller. In the case
  // of a number, such as "2.00" where the caller is planning to store it in a
  // Number-type value and hence will call Number on it to avoid storing a
  // string by mistake, it would get converted to 2 even if we hadn't trimmed
  // it ourselves. Then, the caller would re-render the widget with 2 as the
  // value, and the decimal point and zeros would disappear as soon as the
  // user has typed it, making it impossible for them to type anything after
  // it.

  // To avoid this, we pass the fully trimmed value to the caller but keep the
  // partially untrimmed value in an internal state variable. We also keep the
  // caller's value so we know a refresh is needed if the caller changes input
  // on us. We do this by maintaining a valueDisplayOverride state variable,
  // which is an object with two entries: "reported" (what we will be sending
  // back to the caller) and "displayed" (what we will be showing).

  // Note that the caller's value may not be a string (e.g. if caller passes
  // a number) do do all comparisons with a string copy.

  const stringValue = String(value);
  const [valueDisplayOverride, setValueDisplayOverride] = useState({
    reported: stringValue,
    displayed: stringValue,
  });

  useEffect(() => {
    if (stringValue !== valueDisplayOverride.reported)
      setValueDisplayOverride({
        reported: stringValue,
        displayed: stringValue,
      });
  }, [stringValue, valueDisplayOverride.reported]);
  const adjustedElementProps = { ...elementProps };
  if (isCurrency) adjustedElementProps.inputMode = "decimal";

  const onChange = (e, blurring, fromSuggestion = null) => {
    if (setFinished) setFinished(blurring);
    const input = ref.current || e.target;
    if (fromSuggestion) input.value = fromSuggestion.newValue;

    /*
        Changing value while someone is typing causes the cursor to jump to the
        end unless we also reset the selection range after changing the value.
        To know what the appropriate new selection range should be after
        the value's length has potentially changed, we split the value
        into three segments (before selection, selection, after selection),
        some of which may be empty (e.g. selection is empty if there is
        just a regular insertion-point cursor rather than a selection).

        For each segment, a series of corrections are applied: the standard
        space-trimming correction, and any other specified correction
        functions. Because corrections may need to be aware of what they
        have done to previous segments, each is passed a state object as
        well as the segment value. For the first segment, the state object
        is empty, but the correction function may store whatever it wants
        there.

        The standard space-trimming correction operates as follows on
        each segment:

           - initial space is trimmed UNLESS the most recent non-empty
             preceding segment ended with a non-space character

           - multiple spaces are combined into one

        (Trailing spaces are handled later; see below)

        For e-mail fields we disallow all spaces and also don't worry
        about the selection range, since selectionStart / selectionEnd
        are defined in the HTML spec to "not apply" to <input type="email">;
        getting them will return null, and trying to set them will cause
        InvalidStateError to be thrown.

        For password fields we only trim start/end spaces but don't
        compress multiple spaces.

*/

    const isEmail = adjustedElementProps.type === "email";
    const isPassword = adjustedElementProps.type === "password";
    const isNumeric = adjustedElementProps.inputMode === "numeric";

    const selectionDoesNotApply = isEmail;
    // If expanding TrimmedInput to other input types where selection does
    // not apply, add them above. (See
    // https://html.spec.whatwg.org/multipage/input.html#concept-input-apply)

    const standardTrim = (segment, state) => {
      let s = segment;
      if (!state.endedWithNonSpace) s = s.replace(/^\s+/, "");
      // For an empty segment inherit previous state; otherwise, update
      // based on whether or not segment currently ends with a space.
      if (s !== "") state.endedWithNonSpace = !s.match(/\s+$/);

      const noSpaces = isEmail || isNumeric || isCurrency;
      return isPassword ? s : s.replace(/\s+/g, noSpaces ? "" : " ");
    };

    /*
         Removal of trailing spaces is done at the end, after recombining
         segments, and only if blurring (trailing space is acceptable
         if the user is still typing and about to type another word).
    */

    const untrimmed = selectionDoesNotApply
      ? [input.value]
      : [
          input.value.substring(0, input.selectionStart),
          input.value.substring(input.selectionStart, input.selectionEnd),
          input.value.substring(input.selectionEnd, input.value.length),
        ];
    const direction = selectionDoesNotApply ? null : input.selectionDirection;

    const numericCorrection = (segment, state) => {
      return segment.replaceAll(/^0+([0-9])/g, "$1").replaceAll(/[^0-9]/g, "");
    };

    const currencyCorrection = (segment, state) => {
      return segment
        .replaceAll(/^0+([0-9])/g, "$1")
        .replaceAll(/[^0-9.]/g, "")
        .replaceAll(/(\..*?)\..*/g, "$1")
        .replaceAll(/(\...).*/g, "$1");
    };

    if (otherCorrections.length === 0 && isNumeric)
      otherCorrections.push(numericCorrection);
    if (otherCorrections.length === 0 && isCurrency) {
      otherCorrections.push(currencyCorrection);
    }

    const corrections = [standardTrim, ...otherCorrections].map(
      (correction) => {
        return {
          state: {},
          action: correction,
        };
      }
    );

    const trimmed = untrimmed.map((segment) => {
      let s = segment;
      corrections.forEach(({ state, action }) => {
        s = action(s, state);
      });
      return s;
    });

    let newSelectionStart = selectionDoesNotApply ? 0 : trimmed[0].length;
    let newSelectionEnd = selectionDoesNotApply
      ? 0
      : trimmed[0].length + trimmed[1].length;

    let trimmedValue = trimmed.join("");

    // Removing a space, or a decimal point, or decimal point followed by
    // zeros, from the end is done when blurring and also when reporting the
    // value back to the caller

    let fullyTrimmedValue = trimmedValue;
    fullyTrimmedValue = fullyTrimmedValue.replace(/\s+$/, "");
    if (isCurrency) fullyTrimmedValue = fullyTrimmedValue.replace(/\.0*$/, "");
    if (blurring) {
      trimmedValue = fullyTrimmedValue;
      if (newSelectionStart > trimmedValue.length)
        newSelectionStart = trimmedValue.length;
      if (newSelectionEnd > trimmedValue.length)
        newSelectionEnd = trimmedValue.length;
    }

    if (trimmedValue !== input.value) {
      input.value = trimmedValue;
      if (!selectionDoesNotApply) {
        input.selectionStart = newSelectionStart;
        input.selectionEnd = newSelectionEnd;
        input.selectionDirection = direction;
      }
    }

    // Report the fully trimmed value, without any space at the end and
    // without a decimal point (or decimal point followed by zeros) at the end
    // of a currency item, because that's what the value would be after
    // blurring, and, more importantly, in the currency case the decimal point
    // will be removed when recording the change in a Number-type variable,
    // causing the user to be unable to type the desired cents value after it
    // if the caller re-renders the fully trimmed value. Instead, update the
    // caller with the fully trimmed value, but keep the partly trimmed one in
    // our internal valueDisplayOverride state.
    setValueDisplayOverride({
      reported: fullyTrimmedValue,
      displayed: trimmedValue,
    });
    setValue(fullyTrimmedValue);
    if (blurring && setFinishedValue) setFinishedValue(fullyTrimmedValue);
  };
  useEffect(() => {
    if (ref.current && startFocussed) ref.current.focus();
  }, [startFocussed]);

  const displayedValue =
    valueDisplayOverride.reported === stringValue
      ? valueDisplayOverride.displayed
      : stringValue;

  const theInput = autosuggest ? (
    <InputWithAutosuggest
      value={displayedValue}
      inputProps={{
        ref,
        value: displayedValue,
        onChange: (e, fromSuggestion) => onChange(e, false, fromSuggestion),
        onBlur: (e) => onChange(e, true),
      }}
      autosuggest={autosuggest}
    />
  ) : (
    <Input
      {...adjustedElementProps}
      domRef={ref}
      value={displayedValue}
      onChange={(e) => onChange(e, false)}
      onBlur={(e) => onChange(e, true)}
    />
  );
  if (isCurrency) {
    return (
      <Field kind="addons">
        <Button disabled>$</Button>
        <Control>{theInput}</Control>
      </Field>
    );
  } else return theInput;
}

function InputWithAutosuggest({ value, autosuggest, inputProps }) {
  const allSuggestions = autosuggest.suggestions;
  const { max = 30, id } = autosuggest;
  const [suggestions, setSuggestions] = useState([]);
  const testableSuggestions = useMemo(
    () =>
      allSuggestions.map((suggestion) => {
        return { text: suggestion, lctext: toLowercaseAscii(suggestion) };
      }),
    [allSuggestions]
  );

  const getSuggestions = useCallback(
    (text) => {
      const lctext = toLowercaseAscii(text);
      return lctext.length === 0
        ? []
        : testableSuggestions.filter((suggestion) =>
            suggestion.lctext.includes(lctext)
          );
    },
    [testableSuggestions]
  );

  const getSuggestionValue = useCallback((suggestion) => suggestion.text, []);
  const renderSuggestion = useCallback((suggestion) => suggestion.text, []);
  const shouldRenderSuggestions = useCallback(
    (text, reason) => {
      const updatedSuggestions = getSuggestions(text);
      return (
        text.length > 0 &&
        updatedSuggestions.length > 0 &&
        updatedSuggestions.length <= max
      );
    },
    [max, getSuggestions]
  );

  const onSuggestionsFetchRequested = useCallback(
    (arg) => setSuggestions(getSuggestions(arg.value)),
    [setSuggestions, getSuggestions]
  );
  const onSuggestionsClearRequested = useCallback(() => setSuggestions([]), [
    setSuggestions,
  ]);
  inputProps.value = value;
  const theme = {
    ...defaultTheme,
    input: "input",
    suggestionsContainerOpen:
      defaultTheme.suggestionsContainerOpen + " suggestions-container",
  };
  return (
    <Autosuggest
      {...{
        id,
        suggestions,
        getSuggestions,
        onSuggestionsFetchRequested,
        onSuggestionsClearRequested,
        getSuggestionValue,
        renderSuggestion,
        shouldRenderSuggestions,
        inputProps,
        theme,
      }}
    />
  );
}

export default TrimmedInput;
